import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { profileApi, formatApiError, resolveImageUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { User, MapPin, Flag, Trophy, Calendar, Pencil, ArrowLeft, AlertTriangle, Shield } from 'lucide-react';

export default function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    profileApi.get(userId)
      .then((res) => { if (!cancelled) setProfile(res.data); })
      .catch((err) => { if (!cancelled) setError(formatApiError(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const isSelf = me && me.id === userId;
  const isAdmin = me && me.role === 'admin';

  const handleAdminDisable = async () => {
    if (!window.confirm('Toggle profile visibility? This will hide/show the profile to other members.')) return;
    try {
      const res = await profileApi.adminToggleDisabled(userId);
      setProfile((p) => ({ ...p, profile_disabled: res.data.profile_disabled }));
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-4">
        <div>
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
          <h1 className="text-white font-heading text-2xl mb-2">Profile not available</h1>
          <p className="text-gray-400 text-sm mb-4">{error || 'This member hasn\'t set up their profile yet.'}</p>
          <Button onClick={() => navigate(-1)} variant="outline" className="border-[#262626] text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>
        </div>
      </div>
    );
  }

  const joinedStr = profile.joined_at ? new Date(profile.joined_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero band */}
      <div className="relative bg-gradient-to-b from-[#1a1a1a] to-[#0A0A0A] border-b border-[#262626]">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 20%, #FF3B30 0%, transparent 40%), radial-gradient(circle at 80% 60%, #3b82f6 0%, transparent 40%)',
          }}
        />
        <div className="container mx-auto px-4 py-10 relative">
          <button onClick={() => navigate(-1)} className="inline-flex items-center text-gray-400 hover:text-white text-sm mb-4">
            <ArrowLeft className="w-3 h-3 mr-1" /> Back
          </button>
          <div className="flex items-start gap-6 flex-wrap">
            <div className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-[#141414] border-2 border-[#262626] overflow-hidden flex items-center justify-center flex-shrink-0" data-testid="profile-avatar">
              {profile.avatar_url ? (
                <img src={resolveImageUrl(profile.avatar_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <User className="w-16 h-16 text-gray-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="tag text-[#FF3B30] mb-2">Member Profile</p>
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-white" data-testid="profile-display-name">
                {profile.display_name || 'Racer'}
              </h1>
              <div className="flex items-center gap-4 flex-wrap mt-3 text-sm">
                {profile.hometown && (
                  <span className="flex items-center gap-1 text-gray-300">
                    <MapPin className="w-4 h-4 text-gray-500" />{profile.hometown}
                  </span>
                )}
                {profile.local_track && (
                  <span className="flex items-center gap-1 text-gray-300">
                    <Flag className="w-4 h-4 text-gray-500" />{profile.local_track}
                  </span>
                )}
                {joinedStr && (
                  <span className="flex items-center gap-1 text-gray-500">
                    <Calendar className="w-4 h-4" />Member since {joinedStr}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {isSelf && (
                <Link to="/profile/edit">
                  <Button className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="edit-my-profile-btn">
                    <Pencil className="w-4 h-4 mr-2" /> Edit Profile
                  </Button>
                </Link>
              )}
              {isAdmin && !isSelf && (
                <Button variant="outline" onClick={handleAdminDisable} className="border-[#262626] text-white" data-testid="admin-toggle-profile-btn">
                  <Shield className="w-4 h-4 mr-2" />
                  {profile.profile_disabled ? 'Enable profile' : 'Disable profile'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {profile.biggest_win && (
            <Card className="bg-[#141414] border-[#262626] p-5">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5 text-[#FF3B30]" />
                <p className="text-gray-500 text-xs uppercase tracking-wider">Biggest Win / Accomplishment</p>
              </div>
              <p className="text-white font-medium" data-testid="profile-biggest-win">{profile.biggest_win}</p>
            </Card>
          )}
          {profile.local_track && (
            <Card className="bg-[#141414] border-[#262626] p-5">
              <div className="flex items-center gap-2 mb-2">
                <Flag className="w-5 h-5 text-blue-400" />
                <p className="text-gray-500 text-xs uppercase tracking-wider">Home Track</p>
              </div>
              <p className="text-white font-medium">{profile.local_track}</p>
            </Card>
          )}
        </div>

        {profile.bio && (
          <Card className="bg-[#141414] border-[#262626] p-5 mt-5">
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">About</p>
            <p className="text-gray-200 whitespace-pre-wrap leading-relaxed" data-testid="profile-bio">{profile.bio}</p>
          </Card>
        )}

        {!profile.biggest_win && !profile.local_track && !profile.bio && (
          <Card className="bg-[#141414] border-[#262626] p-8 text-center">
            <User className="w-12 h-12 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">
              This member hasn't filled in their profile details yet.
            </p>
            {isSelf && (
              <Link to="/profile/edit" className="inline-block mt-4">
                <Button className="bg-[#FF3B30] hover:bg-[#E6352B]">
                  <Pencil className="w-4 h-4 mr-2" />Complete my profile
                </Button>
              </Link>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
